/**
 * The D5 training round trip (docs/superpowers/specs/2026-10-28-native-budget-mmm-design.md §3.3, §6; plan R8):
 * - the dataset download and the version upload, through /api/mmm-dataset and /api/mmm-versions with the caller's
 *   session token. The routes decide who may (spec O13, O14), so nothing here asks for a role: /admin/mmm-models and
 *   /budget's box 1 share these;
 * - the version list and the activation, the master's (plan R5, O14), on the browser client. RLS and the RPC refuse
 *   anyone else.
 * A route's refusal is its own words, verbatim: the code, then its detail or failing checks, one per line.
 *
 * @module services/budget/budgetAdminService
 */
import { getSupabaseClient } from '@/config';
import { logError } from '@/services/logger';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import type { ServiceResult } from '@/types';
import type { Json } from '@/types/database.types';
import { OUTCOME_METRICS, type OutcomeMetric } from './budgetContract';

/** One row of the master's version list. */
export interface VersionListRow {
  id: string;
  version: number;
  trainedAt: string;
  /** The dataset's week labels (Mondays, plan R1): its data runs from windowStart to windowEnd + 6 days. */
  windowStart: string;
  windowEnd: string;
  outcomeMetric: OutcomeMetric;
  /** diagnostics' r2, mape and max_rhat (bundleSchema's names); null = not a finite number in the stored jsonb. */
  r2: number | null;
  mape: number | null;
  maxRhat: number | null;
  uploadedByEmail: string | null;
  note: string | null;
  isActive: boolean;
  /** How a version is matched to its pkl offline (spec §3.4; the master's to see, §5.11; ruling T17c). */
  pklFileName: string;
  pklSha256: string;
}

/** Every route refusal has `error`; a 422 adds `detail` (an array for bundle_invalid), a 409 its `failing` check ids. */
interface Refusal { error?: string; detail?: string | string[]; failing?: string[] }

/** A refusal keeps a 409's failing check ids in its words, and as data beside them, which box 1 names. */
export type DatasetDownload = ServiceResult<{ fileName: string; text: string }> & { failing?: string[] };

/** insecureContext: the browser refused before anything was posted (ruling T17b), which the form words in the page's language. */
export type VersionUpload = ServiceResult<{ versionId: string; version: number }> & { insecureContext?: true };

/** A version row as read: no bundle (each is hundreds of KB), and its dataset embedded through dataset_id. */
interface VersionDb {
  id: string;
  version: number;
  trained_at: string;
  diagnostics: Json;
  uploaded_by_email: string | null;
  note: string | null;
  is_active: boolean;
  pkl_file_name: string;
  pkl_sha256: string;
  mmm_datasets: { window_start: string; window_end: string; outcome_metric: string } | null;
}

const VERSION_COLUMNS = 'id, version, trained_at, diagnostics, uploaded_by_email, note, is_active, pkl_file_name, pkl_sha256, '
  + 'mmm_datasets(window_start, window_end, outcome_metric)';

const messageOf = (status: number, body: Refusal | null): string =>
  [body?.error ?? `HTTP ${status}`, ...[body?.detail ?? body?.failing ?? []].flat()].join('\n');

/** The failing ids only when they came as a list of strings: the body is unchecked JSON. */
const failingOf = (body: Refusal | null): string[] | undefined => {
  const ids: unknown = body?.failing;
  return Array.isArray(ids) && ids.every((id): id is string => typeof id === 'string') ? ids : undefined;
};

/** The caller's session token, sent as bigQueryJobService sends it; null when signed out. */
const sessionToken = async (): Promise<string | null> => {
  const { data: session } = await getSupabaseClient().auth.getSession();
  return session.session?.access_token ?? null;
};

/**
 * shortcut: hashes the whole pkl in one buffer (WebCrypto cannot stream), so a pkl of several GB could exhaust the tab;
 * hash it in chunks with an incremental SHA-256 once pkls near 1 GB. And WebCrypto exists only in a secure context
 * (https or localhost), so plain-http UAT cannot upload (ruling T17b); serve UAT over https, or hash in pure JS, the day
 * it must.
 */
const sha256Hex = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
};

/** The training dataset, built and recorded by the route (plan R8): its file name and text exactly as served. */
export const downloadDataset = async (workspaceId: string): Promise<DatasetDownload> => {
  try {
    const token = await sessionToken();
    if (!token) return { success: false, error: 'Not signed in' };
    const resp = await fetch(`/api/mmm-dataset?workspace=${encodeURIComponent(workspaceId)}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = (await resp.json().catch(() => null)) as (Refusal & { fileName?: unknown; text?: unknown }) | null;
    if (!resp.ok || typeof body?.fileName !== 'string' || typeof body.text !== 'string') {
      return { success: false, error: messageOf(resp.status, body), failing: failingOf(body) };
    }
    return { success: true, data: { fileName: body.fileName, text: body.text } };
  } catch (e) {
    logError('BudgetAdmin', 'dataset download failed', e);
    return { success: false, error: e instanceof Error ? e.message : 'Download failed' };
  }
};

/** Saves `text` through a Blob link, byte for byte: the dataset's sha256 was taken over exactly these bytes (plan R8). */
export const saveTextFile = (fileName: string, text: string): void => {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  // Appended before clicking, as downloadReport.ts: Firefox ignores a click on a link that is not in the document.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Long enough for the download to start, which is all the URL is needed for.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Uploads a trained bundle; the route stores it as the next version, active at once (plan R7). */
export const uploadVersion = async (bundleFile: File, pkl: File, note: string): Promise<VersionUpload> => {
  // T17b: on plain http the browser has no crypto.subtle, and the hash would fail as a bare TypeError.
  if (!globalThis.crypto?.subtle) {
    return { success: false, error: 'insecure context: hashing model.pkl needs https or localhost', insecureContext: true };
  }
  try {
    const token = await sessionToken();
    if (!token) return { success: false, error: 'Not signed in' };
    const bundle: unknown = JSON.parse(await bundleFile.text());
    // Plan R8: the pkl's sha256 and name only; its bytes never leave the machine. A blank note is left out (NULL).
    const payload = { bundle, pklSha256: await sha256Hex(pkl), pklFileName: pkl.name, note: note.trim() || undefined };
    const resp = await fetch('/api/mmm-versions', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = (await resp.json().catch(() => null)) as (Refusal & { versionId?: unknown; version?: unknown }) | null;
    if (resp.status !== 201 || typeof body?.versionId !== 'string' || typeof body.version !== 'number') {
      return { success: false, error: messageOf(resp.status, body) };
    }
    return { success: true, data: { versionId: body.versionId, version: body.version } };
  } catch (e) {
    logError('BudgetAdmin', 'version upload failed', e);
    // Only the bundle's JSON.parse throws a SyntaxError here: named, so the admin sees which file is not JSON.
    if (e instanceof SyntaxError) return { success: false, error: `${bundleFile.name}: ${e.message}` };
    return { success: false, error: e instanceof Error ? e.message : 'Upload failed' };
  }
};

/** diagnostics[key] when it is a finite number: the column is jsonb, unchecked on read. */
const figureOf = (d: Json, key: 'r2' | 'mape' | 'max_rhat'): number | null => {
  const v = d !== null && typeof d === 'object' && !Array.isArray(d) ? d[key] : undefined;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

const rowOf = (r: VersionDb): VersionListRow => {
  const dataset = r.mmm_datasets;
  // dataset_id is NOT NULL and the master reads every dataset (R5): a missing one is a broken read, not a blank row.
  if (!dataset) throw new Error(`budget versions read failed: v${r.version} came back without its dataset`);
  const metric = OUTCOME_METRICS.find((m) => m === dataset.outcome_metric);
  if (!metric) throw new Error(`budget versions read failed: v${r.version}'s metric "${dataset.outcome_metric}" is not one of ${OUTCOME_METRICS.join(', ')}`);
  return {
    id: r.id, version: r.version, trainedAt: r.trained_at, windowStart: dataset.window_start, windowEnd: dataset.window_end,
    outcomeMetric: metric, r2: figureOf(r.diagnostics, 'r2'), mape: figureOf(r.diagnostics, 'mape'),
    maxRhat: figureOf(r.diagnostics, 'max_rhat'), uploadedByEmail: r.uploaded_by_email, note: r.note, isActive: r.is_active,
    pklFileName: r.pkl_file_name, pklSha256: r.pkl_sha256,
  };
};

/** The workspace's versions, newest first. Throws on a failed page: never a short list. */
export const listVersions = async (workspaceId: string): Promise<VersionListRow[]> => {
  const { rows, error } = await readAllRows(
    (from, to) => getSupabaseClient()
      .from('mmm_model_versions')
      .select(VERSION_COLUMNS)
      .eq('workspace_id', workspaceId)
      .order('version', { ascending: false })
      .order('id', { ascending: true })
      .range(from, to),
    'mmm_model_versions',
  );
  if (error) throw new Error(`budget versions read failed: ${errorMessageOf(error)}`);
  // database.types.ts declares no relationships for mmm_model_versions, so the embed is untyped: the to-one dataset
  // arrives as an object (or null), as api/_lib/mmmVersions.ts reads its workspaces embed.
  return (rows as unknown as VersionDb[]).map(rowOf);
};

/** mmm_set_active_version (SECURITY DEFINER, the master only): the workspace's other versions go inactive in the same call. */
export const setActiveVersion = async (versionId: string): Promise<void> => {
  const { error } = await getSupabaseClient().rpc('mmm_set_active_version', { p_version_id: versionId });
  if (error) throw new Error(`budget version activate failed: ${errorMessageOf(error)}`);
};
